// The Family shell (Paper "Family"): the Crew sidebar and page frame. Data in,
// no router: the active row comes from `pathname`, links go through `Link`.
// Studio renders it from static data, the app from `/api/me`. Below `md` the
// sidebar is a sheet, opened from a top bar, and the main sections are a
// bottom tab bar, the rest under its More. Routines, Activity, Memory and
// People are reached from Settings, not the nav: on their pages Settings is the
// active row.
import type { AgentTint, PersonTint } from "@crew/ui/components/crew-avatar";
import {
  CrewAgentItem,
  CrewComputerStatus,
  CrewNavGroup,
  CrewNavItem,
  CrewUserCard,
  CrewWorkspaceHeader,
  NewDot,
  crewSidebarStyle,
} from "@crew/ui/components/crew-sidebar";
import {
  CrewMobileTopBar,
  CrewTabBar,
  CrewTabButton,
  CrewTopBarAvatar,
  type CrewTab,
} from "@crew/ui/components/crew-mobile-nav";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@crew/ui/components/drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@crew/ui/components/dropdown-menu";
import { Link } from "@crew/ui/components/link";
import {
  AppsIcon,
  ChatsIcon,
  DecisionsIcon,
  MoreIcon,
  DocsIcon,
  SettingsIcon,
  TasksIcon,
  TodayIcon,
  WhatsNewIcon,
  type IconComponent,
} from "@crew/ui/components/nav-icons";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@crew/ui/components/sidebar";
import type { StatusTone } from "@crew/ui/components/status";
import { Button } from "@crew/ui/components/button";
import type { Tone } from "@crew/ui/components/typography";
import { useEdgeSwipeGuard, useSwipeToOpen } from "@crew/ui/hooks/use-swipe";
import { cn } from "@crew/ui/lib/utils";
import { LogOut, MessagesSquare } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

export type FamilyShellAgent = {
  id: string;
  name: string;
  glyph: string;
  tint: AgentTint;
  href: string;
  /** One line under the name, e.g. "Working: this week's bills". */
  status: string;
  statusTone?: Tone;
  /** The computer is awake: a presence dot on the avatar. */
  awake?: boolean;
};

export type FamilyShellArea = {
  id: string;
  label: string;
  href: string;
  icon: IconComponent;
  /** Its open tasks, in the count lane. */
  count?: number;
};

/** The pages a shell can leave out of its nav: the app hides those still to come. */
export type FamilyShellPage = "today" | "decisions" | "apps";

/** The pages Settings links to, outside its tabs: Settings is their nav row. */
const UNDER_SETTINGS = ["/settings", "/routines", "/activity", "/memory", "/people"];

export type FamilyShellComputer = { tone: StatusTone; title: string; detail: string };

/**
 * A page that fills the screen and scrolls inside (Chats, Activity). On a
 * phone it's pinned between the top bar (3.5rem) and the tab bar (4rem), fixed
 * as the tab bar is, so its bottom is the tab bar's top. Never a `100svh`
 * height: iOS can leave the viewport units short after the keyboard closes,
 * and the page then ended that far above the tab bar.
 */
export const FULL_HEIGHT_PAGE =
  "fixed inset-x-0 top-[calc(3.5rem+env(safe-area-inset-top))] bottom-[calc(4rem+env(safe-area-inset-bottom))] flex min-h-0 md:static md:h-svh";

export type FamilyShellProps = {
  /** The current path; picks the active row. */
  pathname: string;
  workspace: { name: string; detail: string };
  user: { name: string; role: string; initials: string; tint: PersonTint };
  agents: FamilyShellAgent[];
  /** The sidebar's computer card; none while the computer needs nothing. */
  computer?: FamilyShellComputer | null;
  /** Areas group; hidden when empty and there's no `onAddArea`. */
  areas?: FamilyShellArea[];
  onAddArea?: () => void;
  onAddAgent?: () => void;
  counts?: { decisions?: number; tasks?: number };
  children: ReactNode;
  /** A right-hand panel (the Today side panel, a task's computer view). */
  panel?: ReactNode;
  /** Makes the user card (and the phone's top-bar avatar) a menu with "Sign out". */
  onLogOut?: () => void;
  /**
   * "What's new" (`/changelog`) under Settings, in the sidebar and the phone's
   * More, with a copper dot while there are updates not seen (on More's tab too).
   */
  whatsNew?: WhatsNew;
  /**
   * The phone's top-bar "Threads" button: opens the side panel where you left off.
   * With a `panel`, a swipe left across the page opens it too.
   */
  onOpenPanel?: () => void;
  /** Pages left out of the sidebar and the phone's tab bar. Studio shows them all. */
  hide?: readonly FamilyShellPage[];
  /** Where the workspace's name links to (the sidebar header, the phone's top bar). Default Today. */
  home?: string;
};

export function FamilyShell({
  pathname,
  workspace,
  user,
  agents,
  computer,
  areas = [],
  onAddArea,
  onAddAgent,
  counts = {},
  children,
  panel,
  onLogOut,
  whatsNew,
  onOpenPanel,
  hide = [],
  home = "/today",
}: FamilyShellProps) {
  const under = (prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`);
  const on = (prefix: string) =>
    prefix === "/settings" ? UNDER_SETTINGS.some(under) : under(prefix);
  const shows = (page: FamilyShellPage) => !hide.includes(page);
  const tabs = phoneTabs(on, counts, shows);

  return (
    <SidebarProvider style={crewSidebarStyle}>
      <CloseSheetOnNavigate pathname={pathname} />
      <Sidebar>
        <SidebarHeader className="px-2 pt-4 pb-2">
          <CrewWorkspaceHeader name={workspace.name} detail={workspace.detail} href={home} />
        </SidebarHeader>
        <SidebarContent className="gap-3">
          <PrimaryNav on={on} counts={counts} shows={shows} />
          <AreasNav areas={areas} onAddArea={onAddArea} on={on} />
          <CrewNavGroup label="Crew" onAdd={onAddAgent}>
            {agents.map((agent) => (
              <CrewAgentItem
                key={agent.id}
                href={agent.href}
                name={agent.name}
                glyph={agent.glyph}
                tint={agent.tint}
                status={agent.status}
                statusTone={agent.statusTone}
                active={on(agent.href)}
                awake={agent.awake}
              />
            ))}
          </CrewNavGroup>
        </SidebarContent>
        <SidebarFooter className="gap-0 px-0 pb-5">
          <ComputerCard computer={computer} />
          <FooterNav on={on} whatsNew={whatsNew} />
          <UserMenu user={user} onLogOut={onLogOut} />
        </SidebarFooter>
      </Sidebar>
      <PageInset onSwipeLeft={onOpenPanel} swipes={Boolean(panel)}>
        <CrewMobileTopBar
          menu={<SidebarTrigger className="size-11" />}
          title={workspace.name}
          titleHref={home}
          actions={
            <>
              {onOpenPanel ? (
                <Button
                  variant="ghost"
                  size="icon-lg"
                  aria-label="Threads"
                  className="size-11"
                  onClick={onOpenPanel}
                >
                  <MessagesSquare />
                </Button>
              ) : null}
              <UserMenu user={user} onLogOut={onLogOut} compact />
            </>
          }
        />
        {children}
        <CrewTabBar tabs={tabs} more={<MoreTab on={on} shows={shows} whatsNew={whatsNew} />} />
      </PageInset>
      {panel}
    </SidebarProvider>
  );
}

/** The phone's bottom tabs: the main sections, less any hidden. */
function phoneTabs(
  on: (prefix: string) => boolean,
  counts: NonNullable<FamilyShellProps["counts"]>,
  shows: (page: FamilyShellPage) => boolean,
): CrewTab[] {
  const tabs: Array<CrewTab & { page?: FamilyShellPage }> = [
    { page: "today", href: "/today", icon: TodayIcon, label: "Today", active: on("/today") },
    { href: "/chats", icon: ChatsIcon, label: "Chats", active: on("/chats") },
    { href: "/tasks", icon: TasksIcon, label: "Tasks", active: on("/tasks") },
    {
      page: "decisions",
      href: "/decisions",
      icon: DecisionsIcon,
      label: "Decisions",
      active: on("/decisions"),
      badge: counts.decisions || undefined,
    },
  ];
  return tabs.filter((tab) => !tab.page || shows(tab.page));
}

/**
 * The page beside the sidebar. On a phone, a swipe right across it opens the
 * sidebar and a swipe left the side panel, from the screen's edges too.
 */
function PageInset({
  onSwipeLeft,
  swipes,
  children,
}: {
  onSwipeLeft?: () => void;
  swipes: boolean;
  children: ReactNode;
}) {
  const page = useRef<HTMLElement>(null);
  const { setOpenMobile } = useSidebar();
  useEdgeSwipeGuard();
  useSwipeToOpen(page, "right", () => setOpenMobile(true), true);
  useSwipeToOpen(page, "left", onSwipeLeft, swipes);
  return (
    // Below `md`, room for the fixed tab bar under the page.
    <SidebarInset
      ref={page}
      className="min-w-0 pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0"
    >
      {children}
    </SidebarInset>
  );
}

/**
 * On a phone the sidebar is a modal sheet (the menu): following a link in it
 * closes it, so the page it opened isn't left behind it.
 */
function CloseSheetOnNavigate({ pathname }: { pathname: string }) {
  const { setOpenMobile } = useSidebar();
  useEffect(() => setOpenMobile(false), [pathname, setOpenMobile]);
  return null;
}

/** The sidebar's computer card, only while the computer needs something. */
function ComputerCard({ computer }: { computer?: FamilyShellComputer | null }) {
  return computer ? <CrewComputerStatus {...computer} /> : null;
}

/**
 * The phone's last tab: the sections the tab bar has no room for, in a sheet
 * that rises from the thumb. No computer status: the home page's card says when the
 * computer needs something.
 */
function MoreTab({
  on,
  shows,
  whatsNew,
}: {
  on: (prefix: string) => boolean;
  shows: (page: FamilyShellPage) => boolean;
  whatsNew?: WhatsNew;
}) {
  const [open, setOpen] = useState(false);
  const rows: Array<{ href: string; icon: IconComponent; label: string; dot?: boolean }> = [
    { href: "/docs", icon: DocsIcon, label: "Docs" },
    ...(shows("apps") ? [{ href: "/apps", icon: AppsIcon, label: "Apps" }] : []),
    { href: "/settings", icon: SettingsIcon, label: "Settings" },
    ...(whatsNew
      ? [{ href: "/changelog", icon: WhatsNewIcon, label: "What's new", dot: whatsNew.unseen }]
      : []),
  ];
  const active = rows.some((row) => on(row.href));
  const dot = Boolean(whatsNew?.unseen);
  return (
    <Drawer open={open} onOpenChange={setOpen} showSwipeHandle>
      <DrawerTrigger
        render={<CrewTabButton tab={{ icon: MoreIcon, label: "More", active, dot }} />}
      />
      <DrawerContent showCloseButton className="pb-[max(1rem,env(safe-area-inset-bottom))]">
        <DrawerHeader>
          <DrawerTitle>More</DrawerTitle>
        </DrawerHeader>
        <nav aria-label="More sections" className="px-2 pt-2 pb-3">
          <ul className="flex flex-col">
            {rows.map((row) => (
              <li key={row.href}>
                <Link
                  href={row.href}
                  aria-current={on(row.href) ? "page" : undefined}
                  onClick={() => setOpen(false)}
                  className={cn(
                    "flex h-12 items-center gap-3 rounded-lg px-3 text-base outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    on(row.href) ? "bg-muted font-medium text-foreground" : "text-copy",
                  )}
                >
                  <row.icon size={20} />
                  <span className="flex-1">{row.label}</span>
                  {row.dot ? <NewDot className="size-2" /> : null}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </DrawerContent>
    </Drawer>
  );
}

type WhatsNew = { unseen: boolean };

/** The sidebar's Areas group; none when there are no areas and no way to add one. */
function AreasNav({
  areas,
  onAddArea,
  on,
}: {
  areas: FamilyShellArea[];
  onAddArea?: () => void;
  on: (prefix: string) => boolean;
}) {
  if (areas.length === 0 && !onAddArea) return null;
  return (
    <CrewNavGroup label="Areas" onAdd={onAddArea}>
      {areas.map((area) => (
        <CrewNavItem
          key={area.id}
          href={area.href}
          icon={area.icon}
          label={area.label}
          count={area.count}
          active={on(area.href)}
        />
      ))}
    </CrewNavGroup>
  );
}

/** The sidebar's footer rows: Settings, What's new. */
function FooterNav({ on, whatsNew }: { on: (prefix: string) => boolean; whatsNew?: WhatsNew }) {
  return (
    <CrewNavGroup className="py-0">
      <CrewNavItem
        href="/settings"
        icon={SettingsIcon}
        label="Settings"
        active={on("/settings")}
        compact
      />
      {whatsNew ? (
        <CrewNavItem
          href="/changelog"
          icon={WhatsNewIcon}
          label="What's new"
          active={on("/changelog")}
          dot={whatsNew.unseen}
          compact
        />
      ) : null}
    </CrewNavGroup>
  );
}

/** The user card in the sidebar, or (`compact`) the avatar in the phone's top bar. */
function UserMenu({
  user,
  onLogOut,
  compact = false,
}: {
  user: FamilyShellProps["user"];
  onLogOut?: () => void;
  compact?: boolean;
}) {
  const { setOpenMobile } = useSidebar();
  const card = compact ? (
    <CrewTopBarAvatar initials={user.initials} tint={user.tint} />
  ) : (
    <CrewUserCard name={user.name} role={user.role} initials={user.initials} tint={user.tint} />
  );
  if (!onLogOut) return card;
  // On a phone the sidebar is a modal sheet: close it, so what signing out shows
  // next (the login page, or its error and Try again) isn't behind it.
  const logOut = () => {
    setOpenMobile(false);
    onLogOut();
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <button
            type="button"
            aria-label="Account"
            className={
              compact
                ? "flex size-11 cursor-pointer items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
                : "block w-full cursor-pointer text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
            }
          />
        }
      >
        {card}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={compact ? "bottom" : "top"}
        align={compact ? "end" : "start"}
        className="min-w-48"
      >
        {compact ? (
          <div className="px-2 py-1.5 text-meta">
            <div className="font-semibold text-foreground">{user.name}</div>
            <div className="text-xs text-muted-foreground">{user.role}</div>
          </div>
        ) : null}
        <DropdownMenuItem onClick={logOut}>
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The pages every crew has: Today through Chats. */
function PrimaryNav({
  on,
  counts,
  shows,
}: {
  on: (prefix: string) => boolean;
  counts: NonNullable<FamilyShellProps["counts"]>;
  shows: (page: FamilyShellPage) => boolean;
}) {
  return (
    <CrewNavGroup>
      {shows("today") ? (
        <CrewNavItem href="/today" icon={TodayIcon} label="Today" active={on("/today")} />
      ) : null}
      {shows("decisions") ? (
        <CrewNavItem
          href="/decisions"
          icon={DecisionsIcon}
          label="Decisions"
          badge={counts.decisions || undefined}
          active={on("/decisions")}
        />
      ) : null}
      <CrewNavItem
        href="/tasks"
        icon={TasksIcon}
        label="Tasks"
        count={counts.tasks}
        active={on("/tasks")}
      />
      <CrewNavItem href="/docs" icon={DocsIcon} label="Docs" active={on("/docs")} />
      {shows("apps") ? (
        <CrewNavItem href="/apps" icon={AppsIcon} label="Apps" active={on("/apps")} />
      ) : null}
      <CrewNavItem href="/chats" icon={ChatsIcon} label="Chats" active={on("/chats")} />
    </CrewNavGroup>
  );
}
